import { signInWithPhoneNumber, PhoneAuthProvider, signInWithCredential } from 'firebase/auth';
import { notifyError } from '../../../../utils/Notify';
import { ERROR_TYPES, FIREBASE_STORAGE_KEYS } from '../utils/constantsNumberLogin';
import { handleFirebaseNumberLoginError, handleOTPVerificationError } from '../utils/errorHandlerNumberLogin';
import { getRemainingWaitTime, clearRateLimit } from './rateLimitService';
import {
  generateUniqueRecaptchaId,
  createHiddenContainer,
  removeElementById,
  prepareCleanRecaptchaEnvironment,
  createRecaptchaVerifier,
  createRecaptchaStatusMonitor,
  createRecaptchaCallbacks,
  cleanupRecaptchaVerifier,
  setCurrentRecaptchaProcess,
  getCurrentRecaptchaProcess,
  clearCurrentRecaptchaProcess
} from '../../../../utils/recaptchaService';
import {
  CLEANUP_DELAY_MS,
  RECAPTCHA_TIMEOUT_MS,
  STATUS_CHECK_INTERVAL_MS,
  RECAPTCHA_STATE_ACTIONS
} from '../utils/constantsNumberLogin';
import { handleRecaptchaError } from '../utils/errorHandlerNumberLogin';



const isTestPhoneNumber = (phoneNumber) => {

  const testPhoneNumbers = [
    '+33611111111',
    '+33611111112',
    '+33611111113',
    '+33611111114',
    '+33613131313',
    '+33612345678',
    '+33777777777',
    '+33615827107'
  ];

  return testPhoneNumbers.includes(phoneNumber);
}

/**
 * Main service for phone number authentication
 * @param {Object} auth - Firebase authentication instance
 * @param {string} number - Phone number
 * @param {Function} setStep - Function to update step
 * @param {string} type - Authentication type
 * @param {string} id - Container ID
 * @param {Function|null} setLoadingCallback - Loading callback
 * @returns {Promise<Object>} Authentication result
 */
export const phoneNumberSignUp = async (auth, number, setStep, type, id, setLoadingCallback = null) => {
  let recaptchaState = null;

  try {
    const waitMinutes = getRemainingWaitTime();
    if (waitMinutes) {
      notifyError(`Veuillez attendre ${waitMinutes} minute(s) avant de réessayer.`);
      return { success: false, error: ERROR_TYPES.RATE_LIMITED };
    }


    if(isTestPhoneNumber(number)) {
      console.info('Test phone number detected');
      auth.settings.appVerificationDisabledForTesting = true;

      const uniqueId = generateUniqueRecaptchaId();
      createHiddenContainer(uniqueId);

      const recaptchaVerifier = createRecaptchaVerifier(uniqueId, auth, {
        onResolved: () => console.log('reCAPTCHA auto-resolved (dev mode)'),
        onExpired: () => {},
        onError: () => {},
        onCancelled: () => {}
      });

      window.recaptchaVerifier = recaptchaVerifier;

      if (setLoadingCallback) {
        setLoadingCallback(false);
      }


      const confirmationResult = await signInWithPhoneNumber(auth, number, recaptchaVerifier);
      window.confirmationResult = confirmationResult;
      sessionStorage.setItem('otp_verification_id', confirmationResult.verificationId);

      clearRateLimit();
      setTimeout(() => removeElementById(uniqueId), 1000);
      return { success: true };
    }


    // Prepare reCAPTCHA environment
    await prepareCleanRecaptchaEnvironment(id, CLEANUP_DELAY_MS);

    const uniqueId = generateUniqueRecaptchaId();
    createHiddenContainer(uniqueId);

    // Initialize reCAPTCHA state
    recaptchaState = { resolved: false, cancelled: false };
    setCurrentRecaptchaProcess(recaptchaState);

    // Create reCAPTCHA verifier
    const callbacks = createRecaptchaCallbacks(recaptchaState, handleRecaptchaError);
    const recaptchaVerifier = createRecaptchaVerifier(uniqueId, auth, callbacks);

    console.log('Rendering reCAPTCHA...');
    window.recaptchaVerifier = recaptchaVerifier;
    await recaptchaVerifier.render();
    console.info('reCAPTCHA rendered successfully');

    const isMockRecaptcha = recaptchaVerifier.type === 'recaptcha'
      && recaptchaVerifier._recaptchaLoader?.constructor?.name === 'MockReCaptchaLoaderImpl';

    console.info('Is Mock reCAPTCHA:', isMockRecaptcha);

    if (setLoadingCallback) {
      setTimeout(() => {
        setLoadingCallback(false);
      }, 7000);
    }

    if (isMockRecaptcha) {
      console.info('Mock reCAPTCHA detected - skipping status monitor');
      const confirmationResult = await signInWithPhoneNumber(auth, number, recaptchaVerifier);
      window.confirmationResult = confirmationResult;
      sessionStorage.setItem('otp_verification_id', confirmationResult.verificationId);

      clearRateLimit();
      setTimeout(() => removeElementById(uniqueId), 1000);

      if (getCurrentRecaptchaProcess() === recaptchaState) {
        clearCurrentRecaptchaProcess();
      }

      console.info('SMS sent successfully with Mock reCAPTCHA');
      return { success: true };
    }

    const statusMonitor = createRecaptchaStatusMonitor(recaptchaState, {
      stateActions: RECAPTCHA_STATE_ACTIONS,
      statusCheckInterval: STATUS_CHECK_INTERVAL_MS,
      timeout: RECAPTCHA_TIMEOUT_MS
    });

    const authPromise = signInWithPhoneNumber(auth, number, recaptchaVerifier);

    const confirmationResult = await Promise.race([
      authPromise.catch(error => {
        recaptchaState.cancelled = true;
        throw error;
      }),
      statusMonitor.then(() => authPromise)
    ]);

    window.confirmationResult = confirmationResult;
    sessionStorage.setItem('otp_verification_id', confirmationResult.verificationId);
    console.info('SMS sent successfully');

    clearRateLimit();
    setTimeout(() => removeElementById(uniqueId), 1000);

    if (getCurrentRecaptchaProcess() === recaptchaState) {
      clearCurrentRecaptchaProcess();
    }


    return { success: true };

  } catch (error) {
    cleanupRecaptchaVerifier();

    if (recaptchaState && getCurrentRecaptchaProcess() === recaptchaState) {
      clearCurrentRecaptchaProcess();
    }

    return handleFirebaseNumberLoginError(error);
  }
};

/**
 * Service to verify OTP code
 * @param {Object} auth - Firebase authentication instance
 * @param {string} otp - OTP code to verify
 * @returns {Promise<Object>} Verification result
 */
export const verifyOTP = async (auth, otp) => {
  try {
    const verificationId =
      window.confirmationResult?.verificationId ||
      sessionStorage.getItem('otp_verification_id');

    if (!verificationId) {
      return { success: false, error: 'code-expired' };
    }

    const credentials = PhoneAuthProvider.credential(verificationId, otp);
    credentials.user = await signInWithCredential(auth, credentials);

    cleanupRecaptchaVerifier();
    sessionStorage.removeItem('otp_verification_id');
    sessionStorage.removeItem('otp_sent_at');

    return {
      success: true,
      user: credentials.user || null,
    };
  } catch (err) {
    return handleOTPVerificationError(err);
  }
};
